// --- Core
import { getUrl } from '../helpers/PlayerHelper.js';

/**
 * Displays a player name with markup so that is can responsively change
 * between a player's fill name on large screns and first initial-last name on
 * small screens
 *
 * @element player-name
 * @attribute {string} first-name Player first name
 * @attribute {string} last-name Player last name
 * @attribute {string} [player-id] Player id for the profile URL
 * @attribute {boolean} [link] Link to the player page (default false)
 * @attribute {boolean} [modal] Plain click opens the player modal instead of following the link.
 * Modifier clicks (cmd, ctrl, shift, alt, middle) still follow the link. Requires `link`.
 * @attribute {boolean} [link-in-new-tab] Open link in a new tab (default true)
 * @attribute {boolean} [full-name] Always render the full first name instead of abbreviating it
 * on small screens (default false)
 *
 * Usage (attribute changes are reactive; multiple synchronous changes coalesce into one render):
 *
 * ```html
 * <player-name first-name="Tom" last-name="Brady" player-id="123" link></player-name>
 * ```
 */
export class PlayerNameElement extends HTMLElement {
    /**
     * @type {boolean}
     */
    _renderPending = false;

    static get observedAttributes() {
        return ['first-name', 'last-name', 'player-id', 'link', 'link-in-new-tab', 'full-name'];
    }

    connectedCallback() {
        this._scheduleRender();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue || !this.isConnected) {
            return;
        }
        this._scheduleRender();
    }

    /**
     * Coalesces multiple synchronous attribute changes (and the initial connect)
     * into a single render on the next microtask
     */
    _scheduleRender() {
        if (this._renderPending) {
            return;
        }
        this._renderPending = true;
        queueMicrotask(() => {
            this._renderPending = false;
            if (!this.isConnected) {
                return;
            }
            this.render();
        });
    }

    /**
     * @returns {{ firstName: string, lastName: string, player_id: string|null }}
     */
    get player() {
        return {
            first_name: this.getAttribute('first-name') ?? '',
            last_name: this.getAttribute('last-name') ?? '',
            player_id: this.getAttribute('player-id') ?? '',
        };
    }

    /**
     * Whether to link to the player's page
     */
    get link() {
        if (!this.hasAttribute('link')) {
            return false;
        }
        const raw = this.getAttribute('link');
        return raw !== 'false' && raw !== '0';
    }
    set link(value) {
        if (value) {
            this.setAttribute('link', '');
        } else {
            this.removeAttribute('link');
        }
    }

    /**
     * Whether a plain click opens the player modal
     */
    get modal() {
        if (!this.hasAttribute('modal')) {
            return false;
        }
        const raw = this.getAttribute('modal');
        return raw !== 'false' && raw !== '0';
    }
    set modal(value) {
        if (value) {
            this.setAttribute('modal', '');
        } else {
            this.removeAttribute('modal');
        }
    }

    /**
     * Whether to open the link in a new tab
     */
    get linkInNewTab() {
        if (!this.hasAttribute('link-in-new-tab')) {
            return true;
        }
        const raw = this.getAttribute('link-in-new-tab');
        return raw !== 'false' && raw !== '0';
    }
    set linkInNewTab(value) {
        if (value) {
            this.setAttribute('link-in-new-tab', '');
        } else {
            this.removeAttribute('link-in-new-tab');
        }
    }

    /**
     * Whether to always show the full first name rather than the responsive initial
     */
    get fullName() {
        if (!this.hasAttribute('full-name')) {
            return false;
        }
        const raw = this.getAttribute('full-name');
        return raw !== 'false' && raw !== '0';
    }
    set fullName(value) {
        if (value) {
            this.setAttribute('full-name', '');
        } else {
            this.removeAttribute('full-name');
        }
    }

    /**
     * Renders the responsive player name markup
     */
    render() {
        const tag = this.link ? 'a' : 'span';
        const wrapper = document.createElement(tag);
        wrapper.className = 'player-name-responsive';

        if (this.link) {
            wrapper.href = getUrl(this.player);
            if (this.linkInNewTab) {
                wrapper.target = '_blank';
                wrapper.rel = 'noopener';
            }
        }

        wrapper.append(buildNameContent(this.player.first_name, this.player.last_name, this.fullName));

        this.style.display = 'contents';
        this.replaceChildren(wrapper);
    }
}

/**
 * @param {string} firstName
 * @param {string} lastName
 * @param {boolean} fullName Whether to skip the small-screen abbreviation of the first name
 * @returns {DocumentFragment}
 */
function buildNameContent(firstName, lastName, fullName = false) {
    const frag = document.createDocumentFragment();

    if (fullName) {
        const firstNameEl = document.createElement('span');
        firstNameEl.className = 'first-name';
        firstNameEl.textContent = firstName;

        const fullLastNameEl = document.createElement('span');
        fullLastNameEl.className = 'last-name';
        fullLastNameEl.textContent = lastName;

        frag.append(firstNameEl, document.createTextNode(' '), fullLastNameEl);
        return frag;
    }

    const firstLetterEl = document.createElement('span');
    firstLetterEl.className = 'first-letter';
    firstLetterEl.textContent = firstName.charAt(0);

    const dotTabletEl = document.createElement('span');
    dotTabletEl.className = 'd-tablet-down';
    dotTabletEl.textContent = '.';

    const restDesktopEl = document.createElement('span');
    restDesktopEl.className = 'd-desktop-up';
    restDesktopEl.textContent = firstName.slice(1);

    const lastNameEl = document.createElement('span');
    lastNameEl.className = 'last-name';
    lastNameEl.textContent = lastName;

    frag.append(firstLetterEl, dotTabletEl, restDesktopEl, document.createTextNode(' '), lastNameEl);

    return frag;
}

// One delegated listener instead of one per element; tables render hundreds of names.
function onPlayerNameClick(event) {
    const host = event.target.closest('player-name');
    if (!host || !host.modal || !host.link) {
        return;
    }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }
    event.preventDefault();
    host.dispatchEvent(new CustomEvent('open-player-modal', { bubbles: true }));
}

const tagName = 'player-name';

if (typeof customElements !== 'undefined' && !customElements.get(tagName)) {
    customElements.define(tagName, PlayerNameElement);
    document.addEventListener('click', onPlayerNameClick);
}
